import React, { useState, useEffect } from 'react';
import { AUTH_API } from '@/config/api';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Loader2, User, Calendar, MapPin, Phone, UserCircle, Heart, ChevronLeft, Upload, Image as ImageIcon, Crown, CreditCard, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { getAuthToken } from '@/utils/auth';
import { getSubscriptionStatus, isSubscriptionActive, getDaysRemaining, isAdmin, clearSubscription, waitForEntitlement } from '@/utils/subscription';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { startSubscriptionCheckout } from '@/utils/payments/subscribe';
import { toast } from '@/hooks/use-toast';

const API = AUTH_API;

export default function ProfilePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { updateUser, user, isAuthenticated, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [showExpiryModal, setShowExpiryModal] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);

  const [formData, setFormData] = useState({
    full_name: '',
    date_of_birth: '',
    age: '',
    city: '',
    country: 'United States',
    phone: '',
    photo_url: '',
    photo_file: null,
    photo_preview: null,
    emergency_contact_name: '',
    emergency_contact_relationship: '',
    emergency_contact_phone: '',
  });

  // Fetch current profile on mount (cookie session + legacy Bearer fallback)
  useEffect(() => {
    const fetchProfile = async () => {
      if (authLoading) return;
      try {
        if (!isAuthenticated && !getAuthToken()) {
          navigate('/authentication');
          return;
        }

        const headers = {};
        const legacy = getAuthToken();
        if (legacy) headers.Authorization = `Bearer ${legacy}`;

        const response = await axios.get(`${API}/me`, {
          withCredentials: true,
          headers,
        });

        const userData = response.data;
        
        // Prefill form with existing data
        setFormData({
          full_name: userData.full_name || '',
          date_of_birth: userData.date_of_birth || '',
          age: userData.age || '',
          city: userData.city || '',
          country: userData.country || 'United States',
          phone: userData.phone || '',
          photo_url: userData.photo_url || '',
          photo_file: null,
          photo_preview: userData.photo_url || null,
          emergency_contact_name: userData.emergency_contact_name || '',
          emergency_contact_relationship: userData.emergency_contact_relationship || '',
          emergency_contact_phone: userData.emergency_contact_phone || '',
        });
      } catch (err) {
        if (err.response?.status === 401) {
          navigate('/authentication');
          return;
        }
        setError('Failed to load profile. Please try again.');
      } finally {
        setFetching(false);
      }
    };

    fetchProfile();
  }, [navigate, isAuthenticated, authLoading]);

  // Fetch subscription status
  useEffect(() => {
    const fetchSubscription = async () => {
      if (user) {
        setSubscriptionLoading(true);
        try {
          if (isAdmin(user)) {
            setSubscription(null);
            setShowExpiryModal(false);
            return;
          }
          const sub = await getSubscriptionStatus(user);
          setSubscription(sub);
          if (sub && !isSubscriptionActive(sub)) {
            setShowExpiryModal(true);
            clearSubscription();
            setSubscription(null);
          } else if (!sub) {
            setShowExpiryModal(true);
          }
        } catch (error) {
          console.error('Error fetching subscription:', error);
        } finally {
          setSubscriptionLoading(false);
        }
      } else {
        setSubscriptionLoading(false);
      }
    };
    
    fetchSubscription();
  }, [user]);

  // Calculate age from date of birth
  const handleDateChange = (e) => {
    const dob = e.target.value;
    setFormData({ ...formData, date_of_birth: dob });
    
    if (dob) {
      const today = new Date();
      const birthDate = new Date(dob);
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      setFormData(prev => ({ ...prev, age: age.toString() }));
    }
  };

  const validateForm = () => {
    if (!formData.full_name || formData.full_name.trim().length < 2) {
      setError('Full name is required (minimum 2 characters)');
      return false;
    }
    if (!formData.date_of_birth) {
      setError('Date of birth is required');
      return false;
    }
    if (!formData.city || formData.city.trim() === '') {
      setError('City is required');
      return false;
    }
    if (!formData.country || formData.country.trim() === '') {
      setError('Country is required');
      return false;
    }
    if (!formData.phone || formData.phone.trim().length < 10) {
      setError('Phone number is required (minimum 10 digits)');
      return false;
    }
    if (!formData.emergency_contact_name || formData.emergency_contact_name.trim() === '') {
      setError('Emergency contact name is required');
      return false;
    }
    if (!formData.emergency_contact_relationship || formData.emergency_contact_relationship.trim() === '') {
      setError('Emergency contact relationship is required');
      return false;
    }
    if (!formData.emergency_contact_phone || formData.emergency_contact_phone.trim().length < 10) {
      setError('Emergency contact phone is required (minimum 10 digits)');
      return false;
    }
    return true;
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size must be less than 5MB');
        return;
      }
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({
          ...formData,
          photo_file: file,
          photo_preview: reader.result,
          photo_url: reader.result, // Use base64 for now, can be uploaded to cloud storage later
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const headers = {};
      const legacy = getAuthToken();
      if (legacy) headers.Authorization = `Bearer ${legacy}`;

      const response = await axios.put(
        `${API}/profile`,
        {
          full_name: formData.full_name,
          date_of_birth: formData.date_of_birth,
          city: formData.city,
          country: formData.country,
          phone: formData.phone,
          photo_url: formData.photo_url || undefined,
          emergency_contact_name: formData.emergency_contact_name,
          emergency_contact_relationship: formData.emergency_contact_relationship,
          emergency_contact_phone: formData.emergency_contact_phone,
        },
        {
          withCredentials: true,
          headers,
        }
      );

      // Update auth context with new user data
      const updatedUser = response.data;
      updateUser(updatedUser);

      setSuccess(true);

      // Redirect after a brief delay
      setTimeout(() => {
        const redirectParam = searchParams.get('redirect');
        const redirectPath = redirectParam ? decodeURIComponent(redirectParam) : '/';
        navigate(redirectPath, { replace: true });
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="min-h-screen bg-cream-paper flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-forest-ink mx-auto mb-3" />
          <p className="text-charcoal">Loading profile...</p>
        </div>
      </div>
    );
  }

  const fieldStyle = {
    background: '#ffffff',
    border: '1px solid #E8ECF2',
    color: '#0f3e17',
    borderRadius: 16,
    height: 48,
  };
  const iconWrap = {
    position: 'absolute',
    left: 14,
    top: '50%',
    transform: 'translateY(-50%)',
    width: 16,
    height: 16,
    color: '#0f3e17',
    pointerEvents: 'none',
    zIndex: 1,
  };
  const paid = !!(subscription && isSubscriptionActive(subscription));
  const admin = !!(user && isAdmin(user));

  return (
    <div className="min-h-screen bg-cream-paper flex items-center justify-center p-4 sm:p-6" style={{ fontFamily: "'Instrument Sans', system-ui, sans-serif" }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-2xl"
      >
        <Card className="w-full bg-paper border-hair rounded-3xl text-forest-ink overflow-visible">
          <CardHeader className="px-6 pt-6">
            <div className="flex items-center gap-3 mb-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/')}
                className="text-forest-ink hover:bg-mist"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
            </div>
            <div className="flex items-start justify-between gap-3">
              <div>
                <CardTitle className="text-2xl text-forest-ink font-semibold">Complete your ByOnco profile</CardTitle>
                <CardDescription className="text-[#3d4a42]">
                  Fill in the required details so we can reach you.
                </CardDescription>
              </div>
              {!subscriptionLoading && admin && (
                <Badge className="bg-mist text-forest-ink border-hair px-3 py-1.5 flex items-center gap-1.5 shrink-0">
                  <Crown className="w-3.5 h-3.5" />
                  Admin
                </Badge>
              )}
              {!subscriptionLoading && paid && !admin && (
                <Badge className="bg-mist text-forest-ink border-hair px-3 py-1.5 flex items-center gap-1.5 shrink-0">
                  <Crown className="w-3.5 h-3.5" />
                  PRO
                </Badge>
              )}
              {!subscriptionLoading && !paid && !admin && (
                <Button
                  type="button"
                  className="shrink-0 h-9 rounded-full px-3 text-xs"
                  style={{ background: '#0f3e17', color: '#fffefc' }}
                  onClick={() => setShowExpiryModal(true)}
                >
                  Get PRO · $19.99
                </Button>
              )}
            </div>
            {!subscriptionLoading && admin && (
              <p className="mt-3 text-sm text-[#3d4a42]">Admin account · full access</p>
            )}
            {!subscriptionLoading && paid && !admin && (
              <p className="mt-3 text-sm text-[#3d4a42]">Plan renews in {getDaysRemaining(subscription)} days</p>
            )}
          </CardHeader>
          <CardContent className="px-6 pb-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <Alert className="text-sm border-hair bg-mist text-forest-ink rounded-2xl">
                  {error}
                </Alert>
              )}

              {success && (
                <Alert className="border-hair bg-mist text-forest-ink rounded-2xl">
                  Profile saved. Redirecting…
                </Alert>
              )}

              <div className="space-y-4">
                <h3 className="text-lg font-medium text-forest-ink flex items-center gap-2">
                  <UserCircle className="w-5 h-5 text-forest-ink" />
                  Basic Details
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="full_name" className="text-forest-ink">Full Name *</Label>
                    <div className="relative">
                      <User style={iconWrap} />
                      <Input
                        id="full_name"
                        type="text"
                        placeholder="Jane Doe"
                        value={formData.full_name}
                        onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                        className="placeholder:text-[#3d4a42] focus-visible:ring-0"
                        style={{ ...fieldStyle, paddingLeft: 42 }}
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="date_of_birth" className="text-forest-ink">Date of Birth *</Label>
                    <div className="relative">
                      <Calendar style={iconWrap} />
                      <Input
                        id="date_of_birth"
                        type="date"
                        value={formData.date_of_birth}
                        onChange={handleDateChange}
                        className="focus-visible:ring-0"
                        style={{ ...fieldStyle, paddingLeft: 42 }}
                        required
                        max={new Date().toISOString().split('T')[0]}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="age" className="text-forest-ink">Age</Label>
                    <Input
                      id="age"
                      type="number"
                      placeholder="Auto-calculated"
                      value={formData.age}
                      readOnly
                      className="placeholder:text-[#3d4a42] focus-visible:ring-0"
                      style={{ ...fieldStyle, background: '#F6F8FB' }}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="photo_file" className="text-forest-ink">Profile Photo (Optional)</Label>
                    <div className="space-y-2">
                      {formData.photo_preview ? (
                        <div className="relative inline-block">
                          <img
                            src={formData.photo_preview}
                            alt="Profile preview"
                            className="w-20 h-20 rounded-full object-cover border border-hair"
                          />
                          <button
                            type="button"
                            onClick={() => setFormData({ ...formData, photo_file: null, photo_preview: null, photo_url: '' })}
                            className="absolute -top-1 -right-1 bg-forest-ink text-cream-paper rounded-full w-6 h-6 flex items-center justify-center text-xs"
                          >
                            ×
                          </button>
                        </div>
                      ) : (
                        <label
                          htmlFor="photo_file"
                          className="flex flex-col items-center justify-center w-full h-32 border border-dashed border-hair rounded-2xl cursor-pointer bg-mist hover:bg-cream-paper transition-colors"
                        >
                          <div className="flex flex-col items-center justify-center pt-5 pb-6">
                            <Upload className="w-7 h-7 mb-2 text-forest-ink" />
                            <p className="mb-1 text-sm text-forest-ink">
                              <span className="font-semibold">Click to upload</span> or drag and drop
                            </p>
                            <p className="text-xs text-[#3d4a42]">PNG, JPG, GIF up to 5MB</p>
                          </div>
                          <input
                            id="photo_file"
                            type="file"
                            accept="image/*"
                            onChange={handlePhotoChange}
                            className="hidden"
                          />
                        </label>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Location & Contact Section */}
              <div className="space-y-4 pt-4 border-t border-hair">
                <h3 className="text-lg font-medium text-forest-ink flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-forest-ink" />
                  Location & Contact
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="city" className="text-forest-ink">City *</Label>
                    <Input
                      id="city"
                      type="text"
                      placeholder="New York"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      className="placeholder:text-[#3d4a42] focus-visible:ring-0"
                      style={fieldStyle}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="country" className="text-forest-ink">Country *</Label>
                    <Input
                      id="country"
                      type="text"
                      placeholder="United States"
                      value={formData.country}
                      onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                      className="placeholder:text-[#3d4a42] focus-visible:ring-0"
                      style={fieldStyle}
                      required
                    />
                  </div>

                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="phone" className="text-forest-ink">Phone Number *</Label>
                    <div className="relative">
                      <Phone style={iconWrap} />
                      <Input
                        id="phone"
                        type="tel"
                        placeholder="+1 (555) 123-4567"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="placeholder:text-[#3d4a42] focus-visible:ring-0"
                        style={{ ...fieldStyle, paddingLeft: 42 }}
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Emergency Contact Section */}
              <div className="space-y-4 pt-4 border-t border-hair">
                <h3 className="text-lg font-medium text-forest-ink flex items-center gap-2">
                  <Heart className="w-5 h-5 text-forest-ink" />
                  Emergency Contact
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="emergency_contact_name" className="text-forest-ink">Contact Name *</Label>
                    <Input
                      id="emergency_contact_name"
                      type="text"
                      placeholder="Jane Doe"
                      value={formData.emergency_contact_name}
                      onChange={(e) => setFormData({ ...formData, emergency_contact_name: e.target.value })}
                      className="placeholder:text-[#3d4a42] focus-visible:ring-0"
                      style={fieldStyle}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="emergency_contact_relationship" className="text-forest-ink">Relationship *</Label>
                    <Input
                      id="emergency_contact_relationship"
                      type="text"
                      placeholder="Spouse, parent, sibling"
                      value={formData.emergency_contact_relationship}
                      onChange={(e) => setFormData({ ...formData, emergency_contact_relationship: e.target.value })}
                      className="placeholder:text-[#3d4a42] focus-visible:ring-0"
                      style={fieldStyle}
                      required
                    />
                  </div>

                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="emergency_contact_phone" className="text-forest-ink">Contact Phone *</Label>
                    <div className="relative">
                      <Phone style={iconWrap} />
                      <Input
                        id="emergency_contact_phone"
                        type="tel"
                        placeholder="+1 (555) 123-4567"
                        value={formData.emergency_contact_phone}
                        onChange={(e) => setFormData({ ...formData, emergency_contact_phone: e.target.value })}
                        className="placeholder:text-[#3d4a42] focus-visible:ring-0"
                        style={{ ...fieldStyle, paddingLeft: 42 }}
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="pt-2">
                <Button
                  type="submit"
                  className="w-full h-12 rounded-full"
                  style={{ background: '#0f3e17', color: '#fffefc' }}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving profile...
                    </>
                  ) : (
                    'Save Profile'
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        <Dialog open={showExpiryModal} onOpenChange={setShowExpiryModal}>
          <DialogContent
            className="border-hair rounded-3xl gap-0 p-0 overflow-hidden sm:max-w-[440px]"
            style={{
              background: '#fffefc',
              color: '#0f3e17',
              fontFamily: "'Instrument Sans', system-ui, sans-serif",
              boxShadow: '0 24px 64px rgba(15, 62, 23, 0.16)',
            }}
          >
            <DialogHeader className="px-6 pt-7 pb-2 text-left space-y-2">
              <div
                className="inline-flex h-10 w-10 items-center justify-center rounded-full"
                style={{ background: '#F6F8FB', border: '1px solid #E8ECF2' }}
              >
                <AlertCircle className="w-5 h-5" style={{ color: '#0f3e17' }} />
              </div>
              <DialogTitle className="text-2xl font-semibold tracking-tight" style={{ color: '#0f3e17' }}>
                Subscription expired
              </DialogTitle>
              <DialogDescription className="text-sm leading-relaxed" style={{ color: '#3d4a42' }}>
                Renew ByOnco PRO to keep hospitals, costs, rare cancers, teleconsultation, and second opinion.
              </DialogDescription>
            </DialogHeader>
            <div className="px-6 pb-6 space-y-4">
              <div
                className="rounded-2xl p-4"
                style={{ background: '#ffffff', border: '1px solid #E8ECF2' }}
              >
                <p className="text-sm font-semibold" style={{ color: '#0f3e17' }}>
                  ByOnco PRO
                </p>
                <p className="text-xl font-semibold mt-1" style={{ color: '#0f3e17' }}>
                  $19.99<span className="text-sm font-medium" style={{ color: '#3d4a42' }}>/month</span>
                </p>
                <p className="text-sm mt-2 leading-relaxed" style={{ color: '#3d4a42' }}>
                  Unlimited companion access. Cancel anytime.
                </p>
              </div>
              <div className="flex flex-col-reverse sm:flex-row gap-2">
                <Button
                  onClick={() => {
                    clearSubscription();
                    setShowExpiryModal(false);
                  }}
                  variant="outline"
                  className="sm:flex-1 h-11 rounded-full border-hair bg-paper hover:bg-mist"
                  style={{ color: '#0f3e17' }}
                >
                  Not now
                </Button>
                <Button
                  onClick={async () => {
                    setPaymentProcessing(true);
                    try {
                      await startSubscriptionCheckout({
                        planType: 'monthly',
                        user,
                        onSuccess: async ({ entitlement } = {}) => {
                          const active = entitlement || await waitForEntitlement(user);
                          setShowExpiryModal(false);
                          toast({
                            variant: 'success',
                            title: active ? 'Subscription renewed' : 'Payment received',
                            description: active
                              ? 'Your subscription is active again.'
                              : 'Access turns on as soon as the bank confirms. Refresh in a moment if needed.',
                          });
                          if (active) window.location.reload();
                          else setPaymentProcessing(false);
                        },
                        onFail: (error) => {
                          toast({
                            variant: 'error',
                            title: 'Payment failed',
                            description: error?.message || 'We could not start checkout. Try again.',
                          });
                          setPaymentProcessing(false);
                        },
                        onDismiss: () => {
                          setPaymentProcessing(false);
                        },
                      });
                    } catch (error) {
                      setPaymentProcessing(false);
                      toast({
                        variant: 'error',
                        title: 'Payment failed',
                        description: error?.message || 'We could not start checkout. Try again.',
                      });
                    }
                  }}
                  disabled={paymentProcessing}
                  className="sm:flex-[1.4] h-11 rounded-full"
                  style={{ background: '#0f3e17', color: '#fffefc' }}
                >
                  {paymentProcessing ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Opening checkout…
                    </>
                  ) : (
                    <>
                      <CreditCard className="mr-2 h-4 w-4" />
                      Renew · $19.99/month
                    </>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </motion.div>
    </div>
  );
}

